import React, { useContext, useEffect, useRef } from 'react';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollWindowForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { OrgTreeContext } from './OrgTreeContext';
import OrgNode from './OrgNode';

function OrgTreeDragDrop({
  orgTree,
  onExpand,
  onCollapse,
  onAddTeams,
  onAddGroup,
  onAddOrgRef,
  onRemove,
  onRename,
  onPromoteToOrg,
  onReparentGroup,
  onMoveTeam,
}) {
  const { uniqueContextId, setActiveDropHighlightKey, setIsOrgTreeDragActive } =
    useContext(OrgTreeContext);
  const lastHighlightKeyRef = useRef(null);

  useEffect(() => {
    return autoScrollWindowForElements();
  }, []);

  useEffect(() => {
    return combine(
      monitorForElements({
        canMonitor: ({ source }) =>
          source.data.uniqueContextId === uniqueContextId,
        onGenerateDragPreview: () => {
          setIsOrgTreeDragActive(true);
        },
        onDragStart: () => {
          setIsOrgTreeDragActive(true);
        },
        onDrag: ({ location }) => {
          const next =
            location.current.dropTargets[0]?.data?.highlightKey ?? null;
          if (next !== lastHighlightKeyRef.current) {
            lastHighlightKeyRef.current = next;
            setActiveDropHighlightKey(next);
          }
        },
        onDrop: ({ source, location }) => {
          lastHighlightKeyRef.current = null;
          setActiveDropHighlightKey(null);
          setIsOrgTreeDragActive(false);

          if (!location.current.dropTargets.length) {
            return;
          }

          const dropTarget = location.current.dropTargets[0];
          const targetNode = dropTarget.data.node;

          if (!targetNode) {
            return;
          }

          if (source.data.type === 'org-node') {
            const { node, fromParentId, pathToNode } = source.data;

            if (fromParentId === targetNode.id) {
              return;
            }

            onReparentGroup({
              childId: node.id,
              fromParentId,
              toParentId: targetNode.id,
              node,
              pathToNode: pathToNode ?? [],
              targetNode,
            });
          } else if (source.data.type === 'team') {
            const { node, fromParentId, pathToNode } = source.data;

            if (fromParentId === targetNode.id) {
              return;
            }

            onMoveTeam({
              teamId: node.id,
              fromOrgId: fromParentId,
              toOrgId: targetNode.id,
              node,
              pathToNode: pathToNode ?? [],
              targetNode,
            });
          }
        },
      })
    );
  }, [
    uniqueContextId,
    setActiveDropHighlightKey,
    setIsOrgTreeDragActive,
    onReparentGroup,
    onMoveTeam,
  ]);

  return (
    <OrgNode
      node={orgTree}
      onExpand={onExpand}
      onCollapse={onCollapse}
      onAddTeams={onAddTeams}
      onAddGroup={onAddGroup}
      onAddOrgRef={onAddOrgRef}
      onRemove={onRemove}
      onRename={onRename}
      onPromoteToOrg={onPromoteToOrg}
    />
  );
}

export default OrgTreeDragDrop;
